import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bot,
  GitBranch,
  Clock,
  Wrench,
  ChevronDown,
  Hospital,
  Film,
  Building,
} from "lucide-react";
import { SORV_PROJECTS } from "../config/sorv-projects";
import { api } from "../lib/api";
import { eventBus } from "../lib/eventBus";
import { timeAgo } from "../lib/format";
import { getAuthToken } from "../lib/api";
import { Send, Bell, BellOff } from "lucide-react";
import type { Agent, WSMessage } from "../lib/types";

const PROJECT_ICONS: Record<string, typeof Hospital> = {
  hospital: Hospital,
  film: Film,
  building: Building,
};

interface SwimLaneProps {
  onAgentClick?: (agent: Agent) => void;
}

export function SwimLane({ onAgentClick }: SwimLaneProps) {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [collapsedProjects, setCollapsedProjects] = useState<Set<string>>(new Set());
  const [sendingProject, setSendingProject] = useState<string | null>(null);
  const [toggles, setToggles] = useState<Record<string, boolean>>({});

  // Load notification toggles
  useEffect(() => {
    async function loadToggles() {
      try {
        const token = getAuthToken();
        const headers: Record<string, string> = {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
        const res = await fetch("/api/notifications/toggles", { headers });
        if (res.ok) {
          const data = await res.json();
          setToggles(data.toggles);
        }
      } catch {}
    }
    loadToggles();
  }, []);

  async function sendProjectSummary(projectId: string) {
    setSendingProject(projectId);
    try {
      const token = getAuthToken();
      await fetch("/api/notifications/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ projectId, type: "summary" }),
      });
    } catch {}
    setTimeout(() => setSendingProject(null), 2000);
  }

  async function toggleAgentNotif(agentId: string) {
    const current = toggles[agentId] !== false; // default true
    const newVal = !current;
    setToggles(prev => ({ ...prev, [agentId]: newVal }));
    try {
      const token = getAuthToken();
      await fetch(`/api/notifications/toggle/${agentId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ enabled: newVal }),
      });
    } catch {
      setToggles(prev => ({ ...prev, [agentId]: current }));
    }
  }

  const load = useCallback(async () => {
    try {
      const [working, connected, idle] = await Promise.all([
        api.agents.list({ status: "working", limit: 100 }),
        api.agents.list({ status: "connected", limit: 100 }),
        api.agents.list({ status: "idle", limit: 100 }),
      ]);
      setAgents([...working.agents, ...connected.agents, ...idle.agents]);
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    return eventBus.subscribe((msg: WSMessage) => {
      if (
        msg.type === "agent_created" ||
        msg.type === "agent_updated" ||
        msg.type === "session_created"
      ) {
        load();
      }
    });
  }, [load]);

  function toggleProject(projectId: string) {
    setCollapsedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  }

  function statusColor(status: string): string {
    switch (status) {
      case "working":
        return "bg-emerald-500";
      case "connected":
        return "bg-blue-400";
      case "idle":
        return "bg-gray-500";
      default:
        return "bg-gray-600";
    }
  }

  function statusLabel(status: string): string {
    switch (status) {
      case "working":
        return "WORKING";
      case "connected":
        return "CONNECTED";
      case "idle":
        return "IDLE";
      default:
        return status.toUpperCase();
    }
  }

  return (
    <div className="flex gap-4 min-h-[500px] overflow-x-auto pb-4">
      {SORV_PROJECTS.map((project) => {
        const Icon = PROJECT_ICONS[project.icon] || Bot;
        const isCollapsed = collapsedProjects.has(project.id);

        // Match agents to this project's configured agents
        const projectAgentNames = project.agents.map((a) => a.name.toLowerCase());
        const matchedAgents = agents.filter(
          (a) =>
            a.name.toLowerCase().includes(project.id) ||
            projectAgentNames.some(
              (name) =>
                a.name.toLowerCase().includes(name) ||
                a.task?.toLowerCase().includes(project.id),
            ),
        );

        return (
          <div
            key={project.id}
            className="bg-surface-1 rounded-xl border border-border flex flex-col flex-shrink-0"
            style={{
              width: isCollapsed ? "3rem" : "18rem",
              borderTopColor: project.color,
              borderTopWidth: "3px",
            }}
          >
            {/* Project header */}
            <button
              onClick={() => toggleProject(project.id)}
              className="flex items-center gap-2 p-3 hover:bg-surface-3 transition-colors rounded-t-xl"
            >
              <Icon className="w-4 h-4 flex-shrink-0" style={{ color: project.color }} />
              {!isCollapsed && (
                <>
                  <span
                    className="text-xs font-bold uppercase tracking-wider truncate"
                    style={{ color: project.color }}
                  >
                    {project.name.split("/")[0]?.trim() ?? project.name}
                  </span>
                  <span className="ml-auto text-[11px] text-gray-600 bg-surface-3 px-2 py-0.5 rounded-full min-w-[1.5rem] text-center">
                    {matchedAgents.length || project.agents.length}
                  </span>
                  <ChevronDown className="w-3 h-3 text-gray-500" />
                </>
              )}
            </button>

            {/* Project actions */}
            {!isCollapsed && (
              <div className="px-2 pb-1">
                <button
                  onClick={(e) => { e.stopPropagation(); sendProjectSummary(project.id); }}
                  disabled={sendingProject === project.id}
                  className="w-full flex items-center justify-center gap-1.5 text-[10px] py-1.5 rounded-md border border-border hover:bg-surface-3 transition-colors text-gray-400 hover:text-accent disabled:opacity-50"
                  title="Enviar resumen a Telegram"
                >
                  <Send className="w-3 h-3" />
                  {sendingProject === project.id ? "Enviado!" : "Enviar resumen"}
                </button>
              </div>
            )}
            {/* Agent cards */}
            {!isCollapsed && (
              <div className="flex-1 p-2 space-y-2 overflow-y-auto">
                {/* Show configured agents (SORV project agents) with their status */}
                {project.agents.map((configAgent) => {
                  // Try to find a live agent that matches
                  const liveAgent = matchedAgents.find(
                    (a) =>
                      a.name.toLowerCase().includes(configAgent.name.toLowerCase()) ||
                      a.task?.toLowerCase().includes(configAgent.id),
                  );

                  return (
                    <div
                      key={configAgent.id}
                      className="bg-surface-3 border border-border rounded-lg p-3 cursor-pointer hover:border-border-light transition-colors"
                      onClick={() => {
                        if (liveAgent) {
                          if (onAgentClick) onAgentClick(liveAgent);
                          else navigate(`/sessions/${liveAgent.session_id}`);
                        }
                      }}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium text-gray-200 truncate">
                          {configAgent.name}
                        </span>
                        {liveAgent ? (
                          <span className="flex items-center gap-1">
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${statusColor(liveAgent.status)} ${
                                liveAgent.status === "working" ? "animate-pulse-dot" : ""
                              }`}
                            />
                            <span
                              className={`text-[10px] font-medium ${
                                liveAgent.status === "working"
                                  ? "text-emerald-400"
                                  : liveAgent.status === "connected"
                                    ? "text-blue-400"
                                    : "text-gray-500"
                              }`}
                            >
                              {statusLabel(liveAgent.status)}
                            </span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-600" />
                            <span className="text-[10px] text-gray-600">IDLE</span>
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <p className="text-[11px] text-gray-500 line-clamp-1 flex-1">{configAgent.trigger}</p>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleAgentNotif(configAgent.id); }}
                          className="ml-2 p-1 rounded hover:bg-surface-4 transition-colors flex-shrink-0"
                          title={toggles[configAgent.id] !== false ? "Notificaciones activas" : "Notificaciones pausadas"}
                        >
                          {toggles[configAgent.id] !== false ? (
                            <Bell className="w-3 h-3 text-cyan-400" />
                          ) : (
                            <BellOff className="w-3 h-3 text-gray-600" />
                          )}
                        </button>
                      </div>

                      {liveAgent && (
                        <div className="mt-2 flex items-center gap-3 text-[10px] text-gray-500">
                          {liveAgent.current_tool && (
                            <span className="flex items-center gap-1">
                              <Wrench className="w-2.5 h-2.5" />
                              {liveAgent.current_tool}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-2.5 h-2.5" />
                            {timeAgo(liveAgent.updated_at || liveAgent.started_at)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Show any unmatched live agents */}
                {matchedAgents
                  .filter(
                    (a) =>
                      !project.agents.some(
                        (ca) =>
                          a.name.toLowerCase().includes(ca.name.toLowerCase()) ||
                          a.task?.toLowerCase().includes(ca.id),
                      ),
                  )
                  .map((agent) => (
                    <div
                      key={agent.id}
                      className="bg-surface-3 border border-border rounded-lg p-3 cursor-pointer hover:border-border-light transition-colors"
                      onClick={() => {
                        if (onAgentClick) onAgentClick(agent);
                        else navigate(`/sessions/${agent.session_id}`);
                      }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-200 truncate flex items-center gap-1.5">
                          <GitBranch className="w-3 h-3 text-violet-400" />
                          {agent.name}
                        </span>
                        <span className="flex items-center gap-1">
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${statusColor(agent.status)} ${
                              agent.status === "working" ? "animate-pulse-dot" : ""
                            }`}
                          />
                          <span className="text-[10px] text-gray-500">
                            {statusLabel(agent.status)}
                          </span>
                        </span>
                      </div>
                      {agent.task && (
                        <p className="text-[11px] text-gray-500 line-clamp-1">{agent.task}</p>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
